import { useState, useEffect } from 'react';
import { IconButton, Typography, Box, Stack, Button, Slider, Divider } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ImageIcon from '@mui/icons-material/Image';
import PaletteIcon from '@mui/icons-material/Palette';
import { ConfigDialog } from '../../dummies/ConfigDialog/ConfigDialog';
import ImageUploader from '../ImageUploader/ImageUploader';
import { updateDashboardSettings } from './actions/settingsActions';
import { useBoardColors } from '../../hooks/useBoardColors';

const FONT_OPTIONS = [
    { value: 'default',         label: 'ברירת מחדל',       fontFamily: 'inherit' },
    { value: 'assistant',       label: 'Assistant',          fontFamily: "'Assistant', sans-serif" },
    { value: 'rubik',           label: 'Rubik',              fontFamily: "'Rubik', sans-serif" },
    { value: 'heebo',           label: 'Heebo',              fontFamily: "'Heebo', sans-serif" },
    { value: 'notoSansHebrew',  label: 'Noto Sans Hebrew',   fontFamily: "'Noto Sans Hebrew', sans-serif" },
    { value: 'secularOne',      label: 'Secular One',        fontFamily: "'Secular One', sans-serif" },
    { value: 'alef',            label: 'Alef',               fontFamily: "'Alef', sans-serif" },
    { value: 'miriamLibre',     label: 'Miriam Libre',       fontFamily: "'Miriam Libre', monospace" },
    { value: 'frankRuhlLibre',  label: 'Frank Ruhl Libre',   fontFamily: "'Frank Ruhl Libre', serif" },
    { value: 'notoSerifHebrew', label: 'Noto Serif Hebrew',  fontFamily: "'Noto Serif Hebrew', serif" },
    { value: 'suezOne',         label: 'Suez One',           fontFamily: "'Suez One', serif" },
    { value: 'syne',            label: 'Syne',               fontFamily: "'Syne', sans-serif" },
];

const SHADOW_OPTIONS = [
    { value: 'none',   label: 'ללא',   offsets: null,           alpha: 0 },
    { value: 'subtle', label: 'עדין',  offsets: '0 2px 8px',   alpha: 0.1 },
    { value: 'medium', label: 'בינוני', offsets: '0 4px 20px', alpha: 0.18 },
    { value: 'strong', label: 'חזק',   offsets: '0 8px 36px',  alpha: 0.3 },
];

const BORDER_RADIUS_PRESETS = [
    { value: 0,  label: 'ישר' },
    { value: 12, label: 'עדין' },
    { value: 24, label: 'עגלגל' },
    { value: 35, label: 'עגול' },
    { value: 50, label: 'מלא' },
];

function buildShadow(intensity, hexColor) {
    const opt = SHADOW_OPTIONS.find(o => o.value === intensity);
    if (!opt || !opt.offsets) return 'none';
    const hex = hexColor || '#000000';
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `${opt.offsets} rgba(${r},${g},${b},${opt.alpha})`;
}

const SectionLabel = ({ children, hint }) => (
    <Box mb={1.5}>
        <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.875rem' }}>{children}</Typography>
        {hint && <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.25 }}>{hint}</Typography>}
    </Box>
);

const SettingsButton = ({
    bgColor = '#fcfbfa',
    bgImage = '',
    borderRadius = 35,
    fontFamily = 'default',
    widgetShadow = 'none',
    widgetShadowColor = '#000000',
    dispatch,
    asListItem = false
}) => {
    const [open, setOpen] = useState(false);
    const [bgMode, setBgMode] = useState(bgImage ? 'image' : 'color');

    const [tempBgColor, setTempBgColor] = useState(bgColor);
    const [tempBgImage, setTempBgImage] = useState(bgImage);
    const [tempBorderRadius, setTempBorderRadius] = useState(borderRadius);
    const [tempFontFamily, setTempFontFamily] = useState(fontFamily);
    const [tempWidgetShadow, setTempWidgetShadow] = useState(widgetShadow);
    const [tempWidgetShadowColor, setTempWidgetShadowColor] = useState(widgetShadowColor);

    const boardColors = useBoardColors();

    useEffect(() => {
        if (open) {
            setTempBgColor(bgColor);
            setTempBgImage(bgImage);
            setBgMode(bgImage ? 'image' : 'color');
            setTempBorderRadius(borderRadius);
            setTempFontFamily(fontFamily);
            setTempWidgetShadow(widgetShadow);
            setTempWidgetShadowColor(widgetShadowColor);
        }
    }, [open, bgColor, bgImage, borderRadius, fontFamily, widgetShadow, widgetShadowColor]);

    const handleClose = () => {
        setOpen(false);
    };

    const handleSave = () => {
        dispatch(updateDashboardSettings({
            bgColor: tempBgColor,
            bgImage: bgMode === 'image' ? tempBgImage : '',
            borderRadius: tempBorderRadius,
            fontFamily: tempFontFamily,
            widgetShadow: tempWidgetShadow,
            widgetShadowColor: tempWidgetShadowColor,
        }));
        setOpen(false);
    };

    // ─── Section: Background ──────────────────────────────────────
    const bgSection = (
        <Box>
            <SectionLabel hint="מוחל על כל שטח הלוח">רקע הלוח</SectionLabel>

            {/* Toggle color / image */}
            <Stack direction="row" spacing={0} sx={{ mb: 2, bgcolor: '#f1f5f9', borderRadius: '10px', p: '3px' }}>
                {[
                    { mode: 'color', icon: <PaletteIcon sx={{ fontSize: 16, ml: 0.5 }} />, label: 'צבע' },
                    { mode: 'image', icon: <ImageIcon sx={{ fontSize: 16, ml: 0.5 }} />, label: 'תמונה' },
                ].map(opt => (
                    <Box
                        key={opt.mode}
                        onClick={() => setBgMode(opt.mode)}
                        sx={{
                            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            gap: 0.5, py: 0.75, borderRadius: '8px', cursor: 'pointer',
                            bgcolor: bgMode === opt.mode ? 'white' : 'transparent',
                            boxShadow: bgMode === opt.mode ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                            color: bgMode === opt.mode ? '#1e293b' : '#94a3b8',
                            fontWeight: bgMode === opt.mode ? 600 : 400,
                            fontSize: '0.8rem',
                            transition: 'all 0.15s',
                        }}
                    >
                        {opt.icon}
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 'inherit', color: 'inherit' }}>{opt.label}</Typography>
                    </Box>
                ))}
            </Stack>

            {bgMode === 'color' ? (
                <Stack spacing={1.5}>
                    <Box
                        component="input"
                        type="color"
                        value={tempBgColor}
                        onChange={e => setTempBgColor(e.target.value)}
                        sx={{ width: '100%', height: 56, borderRadius: '10px', border: '1.5px solid #e2e8f0', cursor: 'pointer', display: 'block' }}
                    />
                    {boardColors.length > 0 && (
                        <Box>
                            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mb: 0.75 }}>צבעי הלוח</Typography>
                            <Stack direction="row" flexWrap="wrap" gap={0.75}>
                                {boardColors.map(c => (
                                    <Box key={c} onClick={() => setTempBgColor(c)} title={c}
                                        sx={{
                                            width: 28, height: 28, borderRadius: 1, cursor: 'pointer',
                                            bgcolor: c,
                                            border: tempBgColor === c ? '2px solid #1e293b' : '1px solid #e2e8f0',
                                            transition: 'all 0.15s', '&:hover': { borderColor: '#94a3b8' },
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    )}
                    <Button
                        variant="text" size="small"
                        onClick={() => setTempBgColor('#fcfbfa')}
                        sx={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'none', alignSelf: 'flex-start' }}
                    >
                        איפוס לברירת מחדל
                    </Button>
                </Stack>
            ) : (
                <ImageUploader
                    currentImage={tempBgImage}
                    onImageChange={url => setTempBgImage(url || '')}
                    folder="dashboard-backgrounds"
                    maxSizeMB={5}
                    showPreview={true}
                    fullWidth={true}
                />
            )}
        </Box>
    );

    // ─── Section: Border Radius ───────────────────────────────────
    const borderRadiusSection = (
        <Box>
            <SectionLabel hint="עיגול הפינות של כל הרכיבים בלוח">עיגול פינות רכיבים</SectionLabel>
            <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{ flex: 1 }}>
                        <Slider
                            value={tempBorderRadius}
                            onChange={(_, val) => setTempBorderRadius(val)}
                            min={0} max={50} step={1}
                            sx={{
                                color: '#1e3a5f',
                                '& .MuiSlider-thumb': { width: 18, height: 18 },
                            }}
                        />
                    </Box>
                    <Box sx={{
                        minWidth: 48, height: 32, borderRadius: '8px',
                        border: '1.5px solid #e2e8f0', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        bgcolor: '#f8fafc',
                    }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#374151' }}>
                            {tempBorderRadius}px
                        </Typography>
                    </Box>
                </Stack>
                {/* Quick presets */}
                <Stack direction="row" spacing={1}>
                    {BORDER_RADIUS_PRESETS.map(preset => (
                        <Box
                            key={preset.value}
                            onClick={() => setTempBorderRadius(preset.value)}
                            sx={{
                                flex: 1, py: 1, textAlign: 'center',
                                border: tempBorderRadius === preset.value ? '2px solid #1e3a5f' : '1.5px solid #e2e8f0',
                                borderRadius: `${preset.value * 0.5}px`,
                                cursor: 'pointer',
                                bgcolor: tempBorderRadius === preset.value ? '#f0f4ff' : '#f8fafc',
                                transition: 'all 0.15s',
                                '&:hover': { borderColor: '#1e3a5f' },
                            }}
                        >
                            <Typography sx={{
                                fontSize: '0.72rem',
                                color: tempBorderRadius === preset.value ? '#1e3a5f' : '#94a3b8',
                                fontWeight: tempBorderRadius === preset.value ? 700 : 400,
                            }}>
                                {preset.label}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </Stack>
        </Box>
    );

    // ─── Section: Font ────────────────────────────────────────────
    const fontSection = (
        <Box>
            <SectionLabel hint="יחול על כל הטקסטים שאין להם עיצוב ספציפי">פונט</SectionLabel>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1,
            }}>
                {FONT_OPTIONS.map(font => (
                    <Box
                        key={font.value}
                        onClick={() => setTempFontFamily(font.value)}
                        sx={{
                            p: '10px 12px',
                            borderRadius: '10px',
                            border: tempFontFamily === font.value ? '2px solid #1e3a5f' : '1.5px solid #e2e8f0',
                            cursor: 'pointer',
                            bgcolor: tempFontFamily === font.value ? '#f0f4ff' : '#f8fafc',
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: '#1e3a5f' },
                        }}
                    >
                        <Typography sx={{
                            fontFamily: font.fontFamily,
                            fontSize: '1rem',
                            fontWeight: 500,
                            color: '#111827',
                            lineHeight: 1.3,
                        }}>
                            שלום עולם
                        </Typography>
                        <Typography sx={{
                            fontSize: '0.68rem',
                            color: tempFontFamily === font.value ? '#1e3a5f' : '#94a3b8',
                            fontWeight: tempFontFamily === font.value ? 600 : 400,
                            mt: 0.25,
                        }}>
                            {font.label}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );

    // ─── Section: Shadow ──────────────────────────────────────────
    const shadowSection = (
        <Box>
            <SectionLabel hint="מוסיף עומק ותחושת ריחוף לרכיבים">צל רכיבים</SectionLabel>
            <Stack spacing={2}>
                {/* Intensity options */}
                <Stack direction="row" spacing={1}>
                    {SHADOW_OPTIONS.map(opt => {
                        const previewShadow = opt.value === 'none' ? 'none'
                            : buildShadow(opt.value, tempWidgetShadowColor);
                        return (
                            <Box
                                key={opt.value}
                                onClick={() => setTempWidgetShadow(opt.value)}
                                sx={{
                                    flex: 1, p: 1.5, borderRadius: '10px', textAlign: 'center',
                                    border: tempWidgetShadow === opt.value ? '2px solid #1e3a5f' : '1.5px solid #e2e8f0',
                                    cursor: 'pointer',
                                    bgcolor: tempWidgetShadow === opt.value ? '#f0f4ff' : '#f8fafc',
                                    transition: 'all 0.15s',
                                    '&:hover': { borderColor: '#1e3a5f' },
                                }}
                            >
                                <Box sx={{
                                    width: '100%', height: 32, borderRadius: '6px',
                                    bgcolor: 'white', border: '1px solid #f1f5f9',
                                    boxShadow: previewShadow, mb: 0.75,
                                }} />
                                <Typography sx={{
                                    fontSize: '0.72rem',
                                    color: tempWidgetShadow === opt.value ? '#1e3a5f' : '#94a3b8',
                                    fontWeight: tempWidgetShadow === opt.value ? 700 : 400,
                                }}>
                                    {opt.label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Stack>

                {/* Color picker — shown only when shadow is active */}
                {tempWidgetShadow !== 'none' && (
                    <Stack direction="row" alignItems="center" spacing={2}
                        sx={{ pt: 1.5, borderTop: '1px solid #f1f5f9' }}
                    >
                        <Typography sx={{ fontSize: '0.8rem', color: '#64748b', flex: 1 }}>צבע הצל</Typography>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box
                                component="input"
                                type="color"
                                value={tempWidgetShadowColor}
                                onChange={e => setTempWidgetShadowColor(e.target.value)}
                                sx={{
                                    width: 40, height: 32, borderRadius: '8px',
                                    border: '1.5px solid #e2e8f0', cursor: 'pointer',
                                    padding: '2px',
                                }}
                            />
                            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                                {tempWidgetShadowColor.toUpperCase()}
                            </Typography>
                            <Button
                                size="small" variant="text"
                                onClick={() => setTempWidgetShadowColor('#000000')}
                                sx={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'none', minWidth: 0, p: '2px 6px' }}
                            >
                                איפוס
                            </Button>
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </Box>
    );

    const content = (
        <Stack spacing={3.5}>
            {bgSection}
            <Divider sx={{ borderColor: '#f1f5f9' }} />
            {borderRadiusSection}
            <Divider sx={{ borderColor: '#f1f5f9' }} />
            {fontSection}
            <Divider sx={{ borderColor: '#f1f5f9' }} />
            {shadowSection}
        </Stack>
    );

    const button = asListItem ? (
        <IconButton
            onClick={() => setOpen(true)}
            sx={{
                width: '100%', borderRadius: '8px', color: 'white',
                justifyContent: 'flex-start', px: 2, py: 1.5,
                display: 'flex', flexDirection: 'row-reverse',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
        >
            <SettingsIcon sx={{ ml: 1.5 }} />
            <Typography variant="body2" sx={{ flex: 1, textAlign: 'right' }}>הגדרות</Typography>
        </IconButton>
    ) : (
        <IconButton
            onClick={() => setOpen(true)}
            sx={{
                color: '#1e3a5f', border: '1px solid #1e3a5f', borderRadius: '50%',
                p: 1, minHeight: '40px', minWidth: '40px',
                '&:hover': { borderColor: '#2d5a8f', bgcolor: 'rgba(30,58,95,0.04)' }
            }}
        >
            <SettingsIcon sx={{ fontSize: '1.2rem' }} />
        </IconButton>
    );

    return (
        <>
            {button}
            <ConfigDialog
                open={open}
                onClose={handleClose}
                onConfirm={handleSave}
                title="הגדרות לוח"
                confirmText="שמור"
                cancelText="ביטול"
                width={520}
            >
                {content}
            </ConfigDialog>
        </>
    );
};

export default SettingsButton;
